import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreateEthnicityRequest,
  Ethnicity,
  UpdateEthnicityRequest,
} from '@interfaces/ethnicities.interface';

import {
  useCreateEthnicityMutation,
  useUpdateEthnicityMutation,
} from '@api/ethnicities.api';

import useFormEnhanced from '@hooks/useFormEnhanced';

export const UPSERT_ETHNICITY_FORM_MODAL_ID = 'upsertEthnicity';

interface UpsertEthnicityFormProps {
  ethnicity?: Ethnicity;
}

export default function UpsertEthnicityForm({
  ethnicity,
}: UpsertEthnicityFormProps) {
  const initialValues = {
    name: ethnicity?.name || '',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: ['name'],
    },
  });

  const [createEthnicity, { isLoading: isLoadingCreate }] =
    useCreateEthnicityMutation();
  const [updateEthnicity, { isLoading: isLoadingUpdate }] =
    useUpdateEthnicityMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_ETHNICITY_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      if (ethnicity) {
        const data: UpdateEthnicityRequest = {
          id: ethnicity.id,
          body: {
            name: values.name,
          },
        };

        await updateEthnicity(data).unwrap();

        notifications.show({
          title: 'Etnia modificata',
          message: 'Etnia modificata con successo.',
        });
      } else {
        const data: CreateEthnicityRequest = {
          name: values.name,
        };

        await createEthnicity(data).unwrap();

        notifications.show({
          title: 'Etnia creata',
          message: 'Etnia creata con successo.',
        });
      }

      closeModal(UPSERT_ETHNICITY_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="Nome"
          {...form.getInputProps('name')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
